'use client';

import { useEffect, useState } from 'react';
import clsx from 'clsx';
import useNetworkStatus from '@package/hooks/src/useNetworkStatus';

import styles from './OfflineBanner.module.css';
import { BannerState } from './types';

const OfflineBanner = () => {
  const isOnline = useNetworkStatus();
  const [bannerState, setBannerState] = useState<BannerState>({
    isVisible: false,
    isAnimating: false,
    isSuccess: false,
  });

  useEffect(() => {
    let animateTimer: NodeJS.Timeout;
    let hideTimer: NodeJS.Timeout;
    let removeTimer: NodeJS.Timeout;

    if (!isOnline) {
      setBannerState({
        isVisible: true,
        isAnimating: false,
        isSuccess: false,
      });

      animateTimer = setTimeout(() => {
        setBannerState((prev) => ({ ...prev, isAnimating: true }));
      }, 10);
    } else if (bannerState.isVisible && isOnline) {
      setBannerState((prev) => ({ ...prev, isSuccess: true }));

      hideTimer = setTimeout(() => {
        setBannerState((prev) => ({ ...prev, isAnimating: false }));
        removeTimer = setTimeout(() => {
          setBannerState({
            isVisible: false,
            isAnimating: false,
            isSuccess: false,
          });
        }, 500);
      }, 3000);
    }

    return () => {
      clearTimeout(animateTimer);
      clearTimeout(hideTimer);
      clearTimeout(removeTimer);
    };
  }, [isOnline, bannerState.isVisible]);

  if (!bannerState.isVisible) return null;

  return (
    <>
      {!isOnline && <div className={styles.offlineOverlay} aria-hidden="true" />}

      <div
        className={clsx(
          styles.offlineBanner,
          bannerState.isAnimating && styles.show,
          bannerState.isSuccess && styles.success,
        )}
        role="alert"
        aria-live="polite"
      >
        <div className={styles.container}>
          <div className={styles.content}>
            <span className={styles.icon}>{bannerState.isSuccess ? '✓' : '⚠️'}</span>
            <span className={styles.message}>
              {bannerState.isSuccess
                ? 'You are back online!'
                : 'You are currently offline. Some features may be unavailable.'}
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default OfflineBanner;
